'use client';

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { FaTrashAlt } from 'react-icons/fa';
import { useVirtualizer } from '@tanstack/react-virtual';

export type ComboboxOption = {
    label: string;
    value: string;
};

export function Combobox({
    options = [],
    placeholder,
    disabled,
    onChange,
    value,
    modal,
    reverseColoring,
    btnClassName,
    removeHandler,
    trashClassName
}: {
    options: ComboboxOption[];
    placeholder?: string;
    modal?: boolean;
    disabled?: boolean;
    onChange?: (newVa?: string) => void;
    removeHandler?: (data?: undefined) => void;
    value?: string;
    reverseColoring?: boolean;
    btnClassName?: string;
    trashClassName?: string;
}) {
    const [search, setSearch] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [scrollElement, setScrollElement] =
        React.useState<HTMLDivElement | null>(null);

    const filteredOptions = React.useMemo(() => {
        if (!search) {
            return options;
        }
        const lowerCaseSearch = search.toLowerCase();
        return options.filter((option) =>
            option.label.toLowerCase().includes(lowerCaseSearch),
        );
    }, [options, search]);

    const virtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: () => scrollElement,
        estimateSize: () => 30,
        overscan: 100,
    });

    const handleChange = (newValue?: string) => {
        onChange?.(newValue);
    };

    return (
        <Popover open={open} onOpenChange={setOpen} modal={modal}>
            <div
                className="w-full flex justify-between items-stretch gap-2 max-w-full"
                dir="rtl"
            >
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                            'w-full flex justify-between text-xs flex-grow',
                            btnClassName,
                        )}
                        disabled={disabled}
                        aria-label={`سلکتور`}
                    >
                        <span className="truncate sm:!max-w-[230px] xl:!max-w-[230px] lg:!max-w-[170px] max-w-[150px]">
                            {value
                                ? options?.find((item) => item.value === value)
                                      ?.label
                                : placeholder}
                        </span>
                        <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                {value && (
                    <Button
                        variant="outline"
                        className={`${trashClassName} bg-blue hover:!bg-blue hover:!text-white disabled:bg-gray-100 py-1 text-white mt-1 hover:opacity-75 transition-opacity`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleChange(undefined);
                            removeHandler?.();
                        }}
                    >
                        <FaTrashAlt className="size-4 inline-flex" />
                    </Button>
                )}
            </div>
            <PopoverContent className="p-0 min-w-60">
                <Command
                    // filter={(v, s, k) => {
                    //     if (s && k) {
                    //         if (k.some((keyword) => keyword.includes(s))) {
                    //             return 1;
                    //         }
                    //     }
                    //     return 0;
                    // }}
                    shouldFilter={false}
                >
                    <CommandInput
                        dir="rtl"
                        placeholder="جستجو..."
                        value={search}
                        onValueChange={setSearch}
                    />
                    <CommandList
                        ref={(el) => {
                            if (el) {
                                setScrollElement(el);
                            }
                        }}
                        className="h-[11rem] w-full overflow-y-auto" // Corrected classes for scroll and height
                    >
                        <CommandEmpty>موردی یافت نشد.</CommandEmpty>
                        <div
                            className={cn('relative')}
                            style={{
                                height: `${virtualizer.getTotalSize() + 10}px`,
                            }}
                        >
                            {virtualizer
                                .getVirtualItems()
                                .filter((virtualItem) =>
                                    filteredOptions[virtualItem.index].label
                                        .toLowerCase()
                                        .includes((search ?? '').toLowerCase()),
                                )
                                .map((virtualRow) => {
                                    const item =
                                        filteredOptions[virtualRow.index];

                                    return (
                                        <CommandItem
                                            key={virtualRow.key}
                                            data-index={virtualRow.index}
                                            ref={virtualizer.measureElement}
                                            className="absolute w-full px-1 flex justify-between overflow-hidden" // Position for virtualization
                                            style={{
                                                transform: `translateY(${virtualRow.start}px)`, // Position with transform
                                            }}
                                            value={item.value}
                                            keywords={[item.label, item.value]}
                                            dir="rtl"
                                            onSelect={(currentValue) => {
                                                handleChange(
                                                    currentValue === value
                                                        ? undefined
                                                        : item.value,
                                                );
                                                setOpen(false);
                                            }}
                                        >
                                            <div
                                                className={cn(
                                                    'flex items-center justify-start overflow-hidden',
                                                    value === item.value
                                                        ? 'w-[90%]'
                                                        : 'w-full',
                                                )}
                                            >
                                                {item.label
                                                    .split('/')
                                                    .map((part, i, parts) => (
                                                        <span
                                                            key={i}
                                                            className={cn(
                                                                'text-nowrap',
                                                                i !==
                                                                    (reverseColoring
                                                                        ? 0
                                                                        : parts.length -
                                                                          1)
                                                                    ? 'text-gray-500 truncate'
                                                                    : '',
                                                            )}
                                                        >
                                                            {part}
                                                            {i !==
                                                                parts.length -
                                                                    1 && '/'}
                                                        </span>
                                                    ))}
                                            </div>

                                            {value === item.value && (
                                                <Check className="shrink-0 h-4 w-4" />
                                            )}
                                        </CommandItem>
                                    );
                                })}
                        </div>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
